import {
  BrowserAuthError,
  AuthError
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { CLIENT_ID } from "./config";
import { useCallback, useRef } from "react";
import { usePopupBlockerContext } from "./components/PopupBlockerModal";

/**
 * Custom error class for authentication failures
 */
export class AuthenticationError extends Error {
  constructor(
    message: string,
    public readonly originalError?: AuthError,
    public readonly method?: 'silent' | 'popup' | 'redirect'
  ) {
    super(message);
    this.name = 'AuthenticationError';
  }
}

/**
 * Hook that provides token acquisition with fallback strategy:
 * 1. Silent token acquisition (from cache)
 * 2. Popup authentication (if silent fails)
 * 3. Redirect authentication (if popup fails or is blocked)
 * 
 * This prevents form interruption while maintaining robust authentication.
 */
export function useAccessToken() {
  const { instance, accounts } = useMsal();
  const { showPopupBlockerModal } = usePopupBlockerContext();
  
  // Use refs to store pending token request for retry
  const pendingTokenRequestRef = useRef<{
    scopes: string[];
    account: any;
  } | null>(null);
  const pendingResolveRef = useRef<((token: string) => void) | null>(null);
  const pendingRejectRef = useRef<((error: Error) => void) | null>(null);

  const getToken = useCallback(async (): Promise<string> => {
    const scopes = [`${CLIENT_ID}/.default`];
    const account = accounts[0] || instance.getActiveAccount();

    if (!account) {
      // No account available, need to login
      console.warn('[Auth] No account found, initiating login via popup');
      try {
        const response = await instance.loginPopup({ scopes });
        return response.accessToken;
      } catch (popupError) {
        console.warn('[Auth] Popup login failed, falling back to redirect', popupError);
        // Fallback to redirect if popup fails
        instance.loginRedirect({ scopes });
        throw new AuthenticationError(
          'Authentication required. Redirecting to login...',
          popupError as AuthError,
          'redirect'
        );
      }
    }

    const tokenRequest = {
      scopes,
      account: account
    };

    // Step 1: Try silent token acquisition
    try {
      const response = await instance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (silentError) {
      console.info('[Auth] Silent token acquisition failed, attempting popup authentication', silentError);
      // Step 2: Try popup authentication
      try {
        const response = await instance.acquireTokenPopup(tokenRequest);
        return response.accessToken;
      } catch (popupError) {
        // Check if popup was blocked
        const isPopupBlocked =
          popupError instanceof BrowserAuthError &&
          (popupError.errorCode === 'popup_window_error' ||
           popupError.errorCode === 'user_cancelled' ||
           popupError.message?.toLowerCase().includes('popup') ||
           popupError.message?.toLowerCase().includes('blocked'));

        if (isPopupBlocked) {
          console.warn('[Auth] Popup authentication blocked, showing modal to user', popupError);

          // Store the token request for retry
          pendingTokenRequestRef.current = tokenRequest;

          // Show modal and wait for user decision
          return new Promise<string>((resolve, reject) => {
            pendingResolveRef.current = resolve;
            pendingRejectRef.current = reject;

            // Attempts popup auth again; returns whether it succeeded instead of
            // silently falling back so the modal can tell the user it's still blocked.
            const handleAttemptPopup = async (): Promise<boolean> => {
              try {
                console.info('[Auth] Attempting popup authentication again');
                const response = await instance.acquireTokenPopup(tokenRequest);
                pendingResolveRef.current?.(response.accessToken);
                // Clear refs
                pendingTokenRequestRef.current = null;
                pendingResolveRef.current = null;
                pendingRejectRef.current = null;
                return true;
              } catch (retryError) {
                console.warn('[Auth] Popup attempt still blocked', retryError);
                return false;
              }
            };

            // Define redirect callback
            const handleRedirect = () => {
              console.info('[Auth] User chose to continue with redirect');
              try {
                instance.acquireTokenRedirect(tokenRequest);
                pendingRejectRef.current?.(
                  new AuthenticationError(
                    'Redirecting to login...',
                    popupError as AuthError,
                    'redirect'
                  )
                );
              } catch (redirectError) {
                pendingRejectRef.current?.(
                  new AuthenticationError(
                    'Failed to initiate authentication redirect',
                    redirectError as AuthError,
                    'redirect'
                  )
                );
              }
              // Clear refs
              pendingTokenRequestRef.current = null;
              pendingResolveRef.current = null;
              pendingRejectRef.current = null;
            };

            // Show the modal
            showPopupBlockerModal(handleAttemptPopup, handleRedirect);
          });
        } else {
          console.error('[Auth] Popup authentication failed', popupError);
          throw new AuthenticationError(
            'Popup authentication failed. Please try again.',
            popupError as AuthError,
            'popup'
          );
        }
      }
    }
  }, [instance, accounts]);

  return getToken;
}